import { ArrayUtils, get, IS, suID } from "@green24/npm-javascript-utils";
import { E_TariffComponentSetType, model_tariffComponent, model_tariffComponentSet } from "../../models/data-model";

export class TariffUtils {
	static isFree(tariff, userMembershipID) {
		if(!tariff) return true;

		let componentSets = userMembershipID && tariff.componentSets.find(set => get(set, "membership.id") == userMembershipID);
		componentSets = componentSets ? [componentSets] : tariff.componentSets.filter(set => !set.membership);

		return componentSets.filter(set => !set.type || set.type == "POLYFAZER").every(set => this.isComponentSetFree(set));
	}

	static isComponentSetFree(set) {
		return (set.components || []).every(component => this.isComponentFree(component));
	}

	static isComponentSetPrimary(set) {
		return (
			(set.type === E_TariffComponentSetType.POLYFAZER || set.type === null) &&
			!this.isComponentSetRestricted(set)
		);
	}

	static isComponentFree(component) {
		if(component.kwBrackets?.length) {
			return component.kwBrackets.every(bracket => bracket.price == 0);
		}
		return component.price == 0;
	}

	static isComponentSetRestricted(set) {
		return (set.membership || set.temporaryUserSet || (set.geoLocationLimit?.longitude && set.geoLocationLimit?.latitude && set.geoLocationLimit?.radiusMeters))
	}

	/**
	 * Categorize component sets
	 * ---
	 * @param componentSets
	 * @return {{
	 *     polyfazer: model_tariffComponentSet[],
	 *     restricted: model_tariffComponentSet[],
	 *     ocpi: model_tariffComponentSet[],
	 *     hubject: model_tariffComponentSet[],
	 *     sygic: model_tariffComponentSet[],
	 * }}
	 */
	static categorizeComponentSets(componentSets) {
		return ArrayUtils.conditionalSplit(componentSets, {
			restricted: set => this.isComponentSetRestricted(set),
			polyfazer: set => set.type === E_TariffComponentSetType.POLYFAZER || set.type === null,
			ocpi: set => set.type === E_TariffComponentSetType.OCPI,
			hubject: set => set.type === E_TariffComponentSetType.HUBJECT,
			sygic: set => set.type === E_TariffComponentSetType.SYGIC,
		}, false);
	}

	static categorizeAndProcessComponentSets(componentSets, {showEmpty = false, showFree = true, allowRoaming = true} = {}) {
		if(componentSets) {
			let {polyfazer, restricted, ocpi, hubject, sygic} = this.categorizeComponentSets(componentSets);

			const hasRestrictedTariffs = (showEmpty ? true : !IS.empty(restricted)) && (showFree ? true : restricted.some(set => !TariffUtils.isComponentSetFree(set)));
			const hasOcpiRoaming = (showEmpty ? true : !IS.empty(ocpi)) && (showFree ? true : ocpi.some(set => !TariffUtils.isComponentSetFree(set)));
			const hasHubjectRoaming = (showEmpty ? true : !IS.empty(hubject)) && (showFree ? true : hubject.some(set => !TariffUtils.isComponentSetFree(set)));
			const hasSygicRoaming = (showEmpty ? true : !IS.empty(sygic)) && (showFree ? true : sygic.some(set => !TariffUtils.isComponentSetFree(set)));

			const tariffs = [
				{
					id: -suID(),
					label: app._lang('primaryTariff'),
					tooltip: app._lang('primaryTariffTooltip'),
					componentSets: polyfazer,
					primary: true,
				},
				hasRestrictedTariffs &&
				{
					id: -suID(),
					newSetType: E_TariffComponentSetType.POLYFAZER,
					label: app._lang('tariffsWithRestrictions'),
					tooltip: app._lang('tariffsWithRestrictionsTooltip'),
					componentSets: restricted,
					supportsMultipleComponentSets: true,
				},
				allowRoaming &&
				(hasOcpiRoaming || hasHubjectRoaming || hasSygicRoaming) &&
				{
					id: -suID(),
					label: app._lang('roamingTariffs'),
					tooltip: app._lang('roamingTariffsTooltip'),
					componentSets: [
						...ocpi,
						...hubject,
						...sygic,
					],
				}
			];

			return tariffs.filter(v => !!v);
		}
	}

	/**
	 * Filter conflicting components in a set
	 * ---
	 * Returns a list of components that are conflicting with each other in a single component's set
	 * @param {model_tariffComponent[]} components
	 * @return {model_tariffComponent[]}
	 */
	static filterConflictingComponentsInSet = (components) => {
		return components.filter(component => {
			return components.some(otherComponent => {
				if(component.id != otherComponent.id) {
					return component.type == otherComponent.type && component.unit == otherComponent.unit;
				}
			});
		});
	};

	static getNewComponentSetForEdit(setType) {
		return  {
			...JSON.parse(JSON.stringify(model_tariffComponentSet)),
			id: -suID(),
			type: setType,
			components: [
				this.getNewComponentForEdit(),
			],
		}
	}

	static getNewComponentForEdit(type = model_tariffComponent.type) {
		const component = {...JSON.parse(JSON.stringify(model_tariffComponent))};
		return {
			...component,
			id: -suID(),
			type,
		};
	}
}
